import React, {useCallback, useContext} from 'react';
import {
  TypeImplementation,
  Plugins,
  withMeta,
  withSystem,
  Expressions,
  TypeCheckerState,
} from '@packages/systems/wfdl';
import {
  type DesignerStores,
  useDesignerSelector,
} from '@packages/systems/designer/flux';
import {
  updateExpressionAction,
  getBindingLabel,
} from '@packages/systems/ExpressionEditor-utils';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  TUnion,
  TEmptyRecord,
  ELiteral,
  EUnion,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import moment from 'moment-timezone';
import {qualify, typeName, pluginName} from '@packages/systems/wfdl-Qualified';
import {
  View,
  VStack,
  NumberInput,
  Select,
  Text,
} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
import * as DateUtils from '@packages/shared/utils/DateUtils';
import {TDate} from '@packages/systems/plugins/DateTime';
import {ExpressionEditor} from '@packages/systems/ExpressionEditor';
import {getString} from '@packages/utilities/json';
import {parseLengthIntervalAndTense} from '@packages/systems/dynamo/utils/ParamUtils';

const RelativeDateRow = emotionStyled(View)({
  display: 'flex',
  gap: '$2',
  alignItems: 'center',
  width: '164px',
});

const NumberInputWrapper = emotionStyled(View)({
  width: '78px',
});

const SelectWrapper = emotionStyled(View)({
  flex: 1,
});

const selectTimezone = (state: DesignerStores) =>
  state.DataManagerUIStore.timezone || DateUtils.UTC;

const RELATIVE_UNIT_OPTIONS = [
  {label: 'days', value: 'days'},
  {label: 'weeks', value: 'weeks'},
  {label: 'months', value: 'months'},
  {label: 'years', value: 'years'},
];

const RELATIVE_DIRECTION_OPTIONS = [
  {label: 'in the past', value: 'past'},
  {label: 'in the future', value: 'future'},
];

const getMode = (expr: ContextualExpression): string | null => {
  const union = ContextualExpressions.getUnion(stripAnn(expr));
  return union?.tag || null;
};

const getExactDateExpr = (
  expr: ContextualExpression
): ContextualExpression | null => {
  const union = ContextualExpressions.getUnion(stripAnn(expr));
  if (union?.tag !== 'Exact' || !union.value) return null;
  return union.value;
};

const getExactDate = (expr: ContextualExpression): string | undefined => {
  const exactDateExpr = getExactDateExpr(expr);
  if (!exactDateExpr) return undefined;
  const exactDate =
    ContextualExpressions.getLiteral(stripAnn(exactDateExpr)) || undefined;
  return exactDate?.value ? getString(exactDate.value) : undefined;
};

const getRelativeExpr = (
  expr: ContextualExpression
): ContextualExpression | null => {
  const union = ContextualExpressions.getUnion(stripAnn(expr));
  if (union?.tag !== 'Relative' || !union.value) return null;
  return union.value;
};

const getRelativeValues = (
  expr: ContextualExpression
): {
  value: number | undefined;
  unit: string | undefined;
  direction: string | undefined;
} => {
  const literal = ContextualExpressions.getLiteral(stripAnn(expr));
  if (!literal || typeof literal.value !== 'string') {
    return {value: undefined, unit: undefined, direction: undefined};
  }

  const parsedValues = parseLengthIntervalAndTense(literal.value);
  if (!parsedValues) {
    return {value: undefined, unit: undefined, direction: undefined};
  }
  return {
    value: parsedValues.length,
    unit: parsedValues.interval,
    direction: parsedValues.tense.toLowerCase(),
  };
};

const DateConditionRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const timezone = useDesignerSelector(selectTimezone);

  // Custom dispatch that intercepts union tag changes to exact mode
  // and ensures the exact date is set to today's date if invalid.
  const customDispatch: TypeImplementation.ExpressionEditorDispatch =
    useCallback(
      (action) => {
        const setExactToday = () => {
          const todayDate = moment.utc().tz(timezone).format('YYYY-MM-DD');
          dispatch(
            updateExpressionAction(
              EUnion({
                tag: 'Exact',
                value: ELiteral({
                  name: qualify(Plugins.name('DateTime'), typeName('Date')),
                  value: todayDate,
                }),
              })
            )
          );
        };

        // Disconnecting a bound field would otherwise leave the Exact value
        // empty while the input shows a default today's date
        if (
          action.type === 'EXPRESSION_BIND_CHANGED' &&
          action.binding === null
        ) {
          setExactToday();
          return;
        }

        if (action.type === 'EXPRESSION_ACTION') {
          const newExpression = action.payload.newExpression;
          const union = Expressions.getUnion(newExpression);

          // Check if we're switching to exact mode with an invalid date
          if (union && union.tag === 'Exact') {
            const exactDateExpr = union.value;
            if (exactDateExpr) {
              const literal = Expressions.getLiteral(exactDateExpr);
              const dateValue = literal?.value
                ? getString(literal.value)
                : undefined;
              // If the exact date is not valid, set it to today's date
              if (
                !dateValue ||
                !moment(dateValue, 'YYYY-MM-DD', true).isValid()
              ) {
                setExactToday();
                return;
              }
            }
          }
        }

        // For all other cases, pass through to the original dispatch
        dispatch(action);
      },
      [dispatch, timezone]
    );

  return <ExpressionEditor expression={expr} dispatch={customDispatch} />;
};

const RelativeDateRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const {
    value: relativeValue,
    unit: relativeUnit,
    direction: relativeDirection,
  } = getRelativeValues(expr);
  const handleRelativeValueChange = useCallback(
    (value: number | undefined) => {
      if (value === undefined) return;
      const unit = relativeUnit || 'days';
      const direction = relativeDirection || 'past';
      const newText = `${value} ${unit} in the ${direction}`;
      const newLiteral = ELiteral({
        name: qualify(pluginName('DateCondition'), typeName('RelativeDate')),
        value: newText,
      });
      dispatch(updateExpressionAction(newLiteral));
    },
    [dispatch, relativeUnit, relativeDirection]
  );

  const handleRelativeUnitChange = useCallback(
    (unit: string) => {
      const value = relativeValue ?? 1;
      const direction = relativeDirection || 'past';
      const newText = `${value} ${unit} in the ${direction}`;
      const newLiteral = ELiteral({
        name: qualify(pluginName('DateCondition'), typeName('RelativeDate')),
        value: newText,
      });
      dispatch(updateExpressionAction(newLiteral));
    },
    [dispatch, relativeValue, relativeDirection]
  );

  const handleRelativeDirectionChange = useCallback(
    (direction: string) => {
      const value = relativeValue ?? 1;
      const unit = relativeUnit || 'days';
      const newText = `${value} ${unit} in the ${direction}`;
      const newLiteral = ELiteral({
        name: qualify(pluginName('DateCondition'), typeName('RelativeDate')),
        value: newText,
      });
      dispatch(updateExpressionAction(newLiteral));
    },
    [dispatch, relativeValue, relativeUnit]
  );
  return (
    <VStack spacing={2}>
      <RelativeDateRow>
        <NumberInputWrapper>
          <NumberInput
            name="relative-value"
            value={relativeValue ?? 1}
            onChange={handleRelativeValueChange}
            min={1}
            shouldShowTrailingControls
          />
        </NumberInputWrapper>
        <SelectWrapper>
          <Select
            data-automation-id="condition-date-relative-unit-select"
            value={relativeUnit || 'days'}
            onChange={handleRelativeUnitChange}
            options={RELATIVE_UNIT_OPTIONS}
          />
        </SelectWrapper>
      </RelativeDateRow>
      <Select
        data-automation-id="condition-date-relative-direction-select"
        value={relativeDirection || 'past'}
        onChange={handleRelativeDirectionChange}
        options={RELATIVE_DIRECTION_OPTIONS}
      />
    </VStack>
  );
};

const DateConditionRenderSummary = ({expr}: {expr: ContextualExpression}) => {
  const [rootCheckerState] = useContext(TypeCheckerState);
  const mode = getMode(expr);
  const exactDateExpr = getExactDateExpr(expr);
  const exactDate = getExactDate(expr);
  const relativeExpr = getRelativeExpr(expr);

  if (
    mode === 'Exact' &&
    exactDateExpr &&
    ContextualExpressions.isBinding(stripAnn(exactDateExpr))
  ) {
    const label = getBindingLabel(
      rootCheckerState.env.context.values,
      stripAll(exactDateExpr)
    );
    return label ? <Text colorVariant="purple">{label}</Text> : null;
  }

  if (mode === 'Exact' && exactDate !== undefined) {
    return <span>{moment(exactDate, 'YYYY-MM-DD').format('MMM D, YYYY')}</span>;
  }

  if (mode === 'Relative' && relativeExpr) {
    const {
      value: relativeValue,
      unit: relativeUnit,
      direction: relativeDirection,
    } = getRelativeValues(relativeExpr);
    return (
      <span>
        {relativeValue} {relativeUnit} in the {relativeDirection}
      </span>
    );
  }

  if (mode === 'Today') {
    return <span>Today</span>;
  }

  return null;
};

const TRelativeDate = TTypeConstructor(
  qualify(pluginName('DateCondition'), typeName('RelativeDate'))
);

export const plugin = Plugins.PrivilegedPlugin({
  dataTypes: {
    DateCondition: TypeImplementation.Alias({
      type: withMeta(
        {
          disableTruncation: true,
        },
        TUnion({
          Exact: withSystem({isBindable: true}, TDate),
          Relative: TRelativeDate,
          Today: TEmptyRecord,
        })
      ),
      render: DateConditionRender,
      renderSummary: DateConditionRenderSummary,
    }),
    RelativeDate: TypeImplementation.Custom({
      render: RelativeDateRender,
      system: {defaultValue: '1 days in the past'},
    }),
  },
});
